/**
 * L6: the town board's bounties (ประกาศล่า) and the elite table (ตัวหัวหน้า). Data only; the rules are in
 * systems/bounties.ts and systems/elites.ts (docs/design/2026-10-11-gameplay-loop.md, Q4: elites are a tint + a tag).
 */
import type { RegionId } from './regions';

export interface BountyDef {
  readonly id: string;
  readonly region: RegionId;
  /** 'kill': N of a monster family; 'elite': the region's elite (หัวหน้า) of that family. */
  readonly kind: 'kill' | 'elite';
  readonly monster: string;
  readonly n: number;
  /** Thai name of the pocket / place the target lives in. */
  readonly where: string;
  /** A plains pocket id (data/layouts/plains.ts): kills must be made inside its box. */
  readonly pocket?: string;
}

export const BOUNTIES_PER_DAY = 3;

export const BOUNTY_PAY = { qiBase: 40, qiPerRank: 6, silverBase: 30, silverPerRank: 4, eliteMul: 2 } as const;

const k = (id: string, region: RegionId, monster: string, n: number, where: string, pocket?: string): BountyDef => ({
  id, region, kind: 'kill', monster, n, where, ...(pocket ? { pocket } : {}),
});
const el = (id: string, region: RegionId, monster: string, where: string): BountyDef => ({
  id, region, kind: 'elite', monster, n: 1, where,
});

export const BOUNTIES: readonly BountyDef[] = [
  k('c-boar', 'centre', 'boar', 6, 'หมูป่าเขี้ยวเหล็ก', 'K2'),
  k('c-viper', 'centre', 'viper', 4, 'งูพิษหยก', 'K3'),
  k('c-assassin', 'centre', 'assassin', 3, 'นักฆ่าเงาดำ', 'K6'),
  k('c-bear', 'centre', 'bear', 3, 'หมีดำ', 'K8'),
  el('c-elite', 'centre', 'boar', 'ทุ่งศิลากลาง'),
  k('n-snowwolf', 'north', 'snowwolf', 6, 'แดนเขาเมฆขาว'),
  k('n-frostman', 'north', 'frostman', 4, 'แดนเขาเมฆขาว'),
  k('n-tiger', 'north', 'whitetiger', 2, 'แดนเขาเมฆขาว'),
  el('n-elite', 'north', 'snowwolf', 'แดนเขาเมฆขาว'),
  k('w-python', 'west', 'python', 2, 'ลุ่มน้ำสายธาร'),
  k('w-centipede', 'west', 'centipede', 5, 'ลุ่มน้ำสายธาร'),
  k('w-viper', 'west', 'viper', 6, 'ลุ่มน้ำสายธาร'),
  el('w-elite', 'west', 'viper', 'ลุ่มน้ำสายธาร'),
  k('e-scorpion', 'east', 'scorpion', 6, 'แดนทรายชาด'),
  k('e-leopard', 'east', 'leopard', 3, 'แดนทรายชาด'),
  k('e-bat', 'east', 'bloodbat', 5, 'แดนทรายชาด'),
  el('e-elite', 'east', 'scorpion', 'แดนทรายชาด'),
  k('s-fox', 'south', 'foxspirit', 3, 'ป่าโคมใต้'),
  k('s-boar', 'south', 'boar', 6, 'ป่าโคมใต้'),
  k('s-leopard', 'south', 'leopard', 3, 'ป่าโคมใต้'),
  el('s-elite', 'south', 'foxspirit', 'ป่าโคมใต้'),
];

export const ELITE = { rate: 0.02, hp: 3, dmg: 1.5, tag: 'หัวหน้า', goldMul: 4, qiMul: 2 } as const;

/** Families that may become elites in each region (the set table). */
export const ELITE_TABLE: Readonly<Record<RegionId, readonly string[]>> = {
  centre: ['wolf', 'boar', 'viper', 'bandit', 'knifer', 'assassin', 'monk', 'spider', 'bear', 'jiangshi', 'bloodbat', 'python'],
  north: ['snowwolf', 'frostman', 'whitetiger', 'bear'],
  west: ['python', 'centipede', 'viper', 'spider'],
  east: ['scorpion', 'leopard', 'bloodbat', 'jiangshi'],
  south: ['foxspirit', 'boar', 'leopard', 'spider', 'bandit'],
};
